import React from 'react';
import { Link } from 'react-router-dom';
import { FiUsers, FiAlertTriangle, FiBookOpen, FiCalendar, FiChevronRight } from 'react-icons/fi';
import { useTeacher } from '../context/TeacherContext';
import BackendPendingBanner from '../components/BackendPendingBanner';
import './TeacherDashboard.css';

function TeacherDashboard() {
  const {
    teacher,
    loading,
    error,
    classes,
    firstCourseId,
    roster,
    progressSummary,
    atRiskStudents,
    atRiskStub,
    hasAnyOrgAdmin,
  } = useTeacher();

  return (
    <div className="teacher-dashboard">
      <header className="teacher-dashboard__header">
        <h1 className="teacher-dashboard__title">
          Good {getGreeting()}, {teacher.displayName}
        </h1>
        <p className="teacher-dashboard__subtitle">
          {classes.length} active class{classes.length !== 1 ? 'es' : ''} you can administer
        </p>
      </header>

      {error && (
        <div className="teacher-dashboard__error-banner">
          <BackendPendingBanner state="error" message={`Failed to load teacher data: ${error}`} />
        </div>
      )}

      <div className="teacher-dashboard__grid">
        {/* ── Active Classes ─────────────────────────────────────────── */}
        <section className="teacher-dashboard__card" aria-labelledby="classes-heading">
          <div className="teacher-dashboard__card-header">
            <FiBookOpen size={18} className="teacher-dashboard__card-icon teacher-dashboard__card-icon--blue" />
            <h2 id="classes-heading" className="teacher-dashboard__card-title">Active Classes</h2>
          </div>
          {loading ? (
            <BackendPendingBanner state="loading" message="Loading your classes…" />
          ) : classes.length === 0 ? (
            <BackendPendingBanner
              state="empty"
              message="No classes yet."
              hint="Create or be granted admin on a class to see it here."
            />
          ) : (
            <ul className="teacher-dashboard__list">
              {classes.slice(0, 5).map(c => {
                const id = c.endeavorId ?? c.id;
                return (
                  <li key={id} className="teacher-dashboard__schedule-item">
                    <div className="teacher-dashboard__schedule-info">
                      <Link to={`/course/${id}`} className="teacher-dashboard__schedule-name">
                        {c.name || `Class ${id}`}
                      </Link>
                      {c.description && (
                        <span className="teacher-dashboard__schedule-detail">{c.description}</span>
                      )}
                    </div>
                    <FiChevronRight size={14} aria-hidden="true" />
                  </li>
                );
              })}
              {classes.length > 5 && (
                <li className="teacher-dashboard__more">+{classes.length - 5} more</li>
              )}
            </ul>
          )}
        </section>

        {/* ── Students Needing Attention ─────────────────────────────── */}
        <section className="teacher-dashboard__card" aria-labelledby="attention-heading">
          <div className="teacher-dashboard__card-header">
            <FiAlertTriangle size={18} className="teacher-dashboard__card-icon teacher-dashboard__card-icon--red" />
            <h2 id="attention-heading" className="teacher-dashboard__card-title">Students Needing Attention</h2>
          </div>
          {loading ? (
            <BackendPendingBanner state="loading" />
          ) : !hasAnyOrgAdmin ? (
            <BackendPendingBanner
              state="empty"
              message="Available for organization admins."
              hint="Join or lead an organization to see at-risk-student signals."
            />
          ) : atRiskStub && atRiskStudents.length === 0 ? (
            <BackendPendingBanner
              state="pending"
              message="Backend endpoint pending."
              hint="At-risk-student detection is a v1 stub on the data-service."
            />
          ) : atRiskStudents.length === 0 ? (
            <BackendPendingBanner state="empty" message="No students flagged." />
          ) : (
            <ul className="teacher-dashboard__list">
              {atRiskStudents.slice(0, 5).map((s, i) => (
                <li key={s.userId ?? s.studentUserId ?? i} className="teacher-dashboard__attention-item">
                  <span className="teacher-dashboard__attention-name">
                    {formatName(s)}
                  </span>
                  <span className="teacher-dashboard__attention-reason">
                    {formatReasons(s)}
                  </span>
                </li>
              ))}
              {atRiskStudents.length > 5 && (
                <li className="teacher-dashboard__more">
                  +{atRiskStudents.length - 5} more
                </li>
              )}
            </ul>
          )}
        </section>

        {/* ── Class Roster Snapshot ─────────────────────────────────── */}
        <section className="teacher-dashboard__card" aria-labelledby="roster-heading">
          <div className="teacher-dashboard__card-header">
            <FiUsers size={18} className="teacher-dashboard__card-icon teacher-dashboard__card-icon--amber" />
            <h2 id="roster-heading" className="teacher-dashboard__card-title">Class Roster Snapshot</h2>
            {firstCourseId != null && (
              <Link to={`/course/${firstCourseId}`} className="teacher-dashboard__card-link">
                Open class <FiChevronRight size={14} />
              </Link>
            )}
          </div>
          {loading ? (
            <BackendPendingBanner state="loading" />
          ) : firstCourseId == null ? (
            <BackendPendingBanner state="empty" message="No class selected." />
          ) : (
            <div className="teacher-dashboard__stat-block">
              <span className="teacher-dashboard__stat-number">{roster.length}</span>
              <span className="teacher-dashboard__stat-label">
                enrolled student{roster.length !== 1 ? 's' : ''}
                {progressSummary?.averageCompletion != null && (
                  <> · {Math.round(progressSummary.averageCompletion * 100)}% avg completion</>
                )}
              </span>
            </div>
          )}
        </section>

        {/* ── Upcoming Deadlines (no backend source yet) ─────────────── */}
        <section className="teacher-dashboard__card" aria-labelledby="upcoming-heading">
          <div className="teacher-dashboard__card-header">
            <FiCalendar size={18} className="teacher-dashboard__card-icon teacher-dashboard__card-icon--green" />
            <h2 id="upcoming-heading" className="teacher-dashboard__card-title">Upcoming Deadlines</h2>
          </div>
          <BackendPendingBanner
            state="pending"
            message="Backend endpoint pending."
            hint="Aggregated upcoming deadlines across your classes require a new endpoint. See src/teacher/BACKEND_GAPS.md."
          />
        </section>
      </div>
    </div>
  );
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

function formatName(s) {
  const first = s.firstName || s.first_name || '';
  const last = s.lastName || s.last_name || '';
  const combined = [first, last].filter(Boolean).join(' ').trim();
  return combined || s.email || s.username || `Student ${s.userId ?? s.studentUserId ?? '?'}`;
}

function formatReasons(s) {
  if (Array.isArray(s.reasons) && s.reasons.length) return s.reasons.join(', ');
  if (s.reason) return String(s.reason);
  if (s.riskLabel) return String(s.riskLabel);
  return 'Flagged';
}

export default TeacherDashboard;
